import { IParsedTable, IParseError } from '../parse-csv';
import { MAX_PREVIEW_ROWS } from './constants';

export interface IParseJsonOptions {
  maxRows?: number;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const toCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const emptyTable = (errors: IParseError[]): IParsedTable => ({
  header: [],
  rows: [],
  rowLines: [],
  delimiter: '',
  skipped: { comments: 0, empty: 0 },
  truncated: false,
  errors,
});

const flattenProperties = (
  value: unknown,
  index: number,
  errors: IParseError[],
): Record<string, string> => {
  const flat: Record<string, string> = {};

  if (value === null || value === undefined) {
    return flat;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => {
      if (isPlainObject(entry) && typeof entry.name === 'string') {
        flat[entry.name] = toCell(entry.value);
      } else {
        errors.push({
          line: index + 1,
          message: 'A property entry is not shaped like { "name": …, "value": … } and was skipped.',
        });
      }
    });
    return flat;
  }

  if (isPlainObject(value)) {
    Object.keys(value).forEach((name) => {
      flat[name] = toCell(value[name]);
    });
    return flat;
  }

  errors.push({ line: index + 1, message: 'The "properties" field is neither a list nor an object.' });
  return flat;
};

const fromRecordsShape = (
  root: Record<string, unknown>,
  maxRows: number,
): IParsedTable => {
  const errors: IParseError[] = [];
  const header = (root.headers as unknown[]).map(toCell);
  const records = root.records as unknown[];
  const rows: string[][] = [];
  const rowLines: number[] = [];
  let truncated = false;

  records.forEach((record, index) => {
    if (!Array.isArray(record)) {
      errors.push({ line: index + 1, message: 'Record is not a list of values and was skipped.' });
    } else if (rows.length >= maxRows) {
      truncated = true;
    } else {
      rows.push(record.map(toCell));
      rowLines.push(index + 1);
    }
  });

  return {
    header,
    rows,
    rowLines,
    delimiter: '',
    skipped: { comments: 0, empty: 0 },
    truncated,
    errors,
  };
};

const fromObjectList = (list: unknown[], maxRows: number): IParsedTable => {
  const errors: IParseError[] = [];
  const flattened: Array<{ index: number; values: Record<string, string> }> = [];
  const header: string[] = [];

  list.forEach((entry, index) => {
    if (!isPlainObject(entry)) {
      errors.push({ line: index + 1, message: 'Entry is not an object and was skipped.' });
      return;
    }

    const values: Record<string, string> = {};
    Object.keys(entry).forEach((key) => {
      if (key !== 'properties') {
        values[key] = toCell(entry[key]);
      }
    });

    const properties = flattenProperties(entry.properties, index, errors);
    Object.keys(properties).forEach((name) => {
      if (!(name in values)) {
        values[name] = properties[name];
      }
    });

    Object.keys(values).forEach((key) => {
      if (!header.includes(key)) {
        header.push(key);
      }
    });

    flattened.push({ index, values });
  });

  const rows: string[][] = [];
  const rowLines: number[] = [];
  let truncated = false;

  flattened.forEach(({ index, values }) => {
    if (rows.length >= maxRows) {
      truncated = true;
    } else {
      rows.push(header.map((key) => values[key] ?? ''));
      rowLines.push(index + 1);
    }
  });

  return {
    header,
    rows,
    rowLines,
    delimiter: '',
    skipped: { comments: 0, empty: 0 },
    truncated,
    errors,
  };
};

export const parseJsonAssetTypes = (
  input: string,
  options: IParseJsonOptions = {},
): IParsedTable => {
  const maxRows = options.maxRows ?? MAX_PREVIEW_ROWS;

  let root: unknown;
  try {
    root = JSON.parse(input);
  } catch (err) {
    return emptyTable([{ line: 1, message: `Could not read the file as JSON: ${(err as Error).message}` }]);
  }

  if (Array.isArray(root)) {
    return fromObjectList(root, maxRows);
  }

  if (isPlainObject(root)) {
    if (Array.isArray(root.headers) && Array.isArray(root.records)) {
      return fromRecordsShape(root, maxRows);
    }
    if (Array.isArray(root.assetTypes)) {
      return fromObjectList(root.assetTypes, maxRows);
    }
    if (Array.isArray(root.data)) {
      return fromObjectList(root.data, maxRows);
    }
  }

  return emptyTable([{
    line: 1,
    message: 'Expected a list of asset types, or an object with "assetTypes", "data", or "headers" and "records".',
  }]);
};
